import React from 'react';
import { Button } from '@ver-uds/react';
import {
  DashboardProfile,
  DashboardUser,
  EverifyUserRole,
  PhoneNumber,
  User,
  UserStatus,
} from '@vis-auth/vis-user-client';
import { Icon } from '@ver-uds/uswds-react';
import { CompanyName } from 'eve-account-client';
import dateFormat from 'dateformat';
import {
  defaultFieldValue,
  LegacyUserRow,
  UserRow,
  UserUpdateRow,
  UNLINKED_USER_ACCOUNTS_LOCAL_STORAGE_KEY,
} from '../constants/Dashboard';
import StatusTag from '../components/StatusTag/StatusTag';
import Environment from '../constants/Environment';

// This util was copied from eve-user-ui
export function displayTextForName(firstName?: string, lastName?: string, middleInitial?: string): string {
  if (!firstName || !lastName) {
    return defaultFieldValue;
  }
  return `${firstName}${middleInitial ? ` ${middleInitial}.` : ''} ${lastName}`;
}

// This util was copied from eve-user-ui
export function formatPhoneNumber(phoneNumber: string): string {
  const cleaned = `${phoneNumber}`.replace(/\D/g, '');
  // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return defaultFieldValue;
}

// Displays phone number with extension
export function displayTextForPhoneNumber(phoneNumber?: PhoneNumber): string {
  if (!phoneNumber) {
    return defaultFieldValue;
  }
  return `${formatPhoneNumber(phoneNumber.number)}${phoneNumber.extension ? ` ext ${phoneNumber.extension}` : ''}`;
}

// This util was copied from eve-user-ui
export function displayTextForEverifyUserRole(role: string): string {
  switch (role) {
    case EverifyUserRole.GENERAL:
      return 'General User';
    case EverifyUserRole.CORPORATEADMIN:
      return 'Corporate Administrator';
    case EverifyUserRole.PROGRAMADMIN:
      return 'Program Administrator';
    case EverifyUserRole.WEBSERVICE:
      return 'Web Services Access';
    case EverifyUserRole.UNKNOWN:
      return 'Unknown';
    default:
      return defaultFieldValue;
  }
}

// This util will get the legacy idp url depending on the environment name
export function getIDPUrl(environmentName: Environment): string {
  if (environmentName === Environment.PROD || environmentName === Environment.PREVIEW) {
    return `https://${environmentName === Environment.PREVIEW ? `${environmentName}.` : ''}idp.uscis.gov`;
  }
  return `https://${environmentName}.idp.vis.uscis.dhs.gov`;
}

const USER_ACCOUNT_ICONS: Record<string, string> = {
  [UserStatus.ACTIVE]: 'login',
  [UserStatus.PENDINGACTIVATION]: 'autorenew',
  [UserStatus.DEACTIVATED]: 'autorenew',

  // Deprecated post-MFA
  [UserStatus.PENDINGREACTIVATION]: 'autorenew',
  [UserStatus.LOCKED]: 'autorenew',
  [UserStatus.PASSWORDCHANGEREQUIRED]: 'autorenew',
  [UserStatus.UNKNOWN]: 'autorenew',

  // This should never get hit as deleted accounts are filtered out
  [UserStatus.DELETED]: '',
};

const USER_ACCOUNT_TEXT: Record<string, string> = {
  [UserStatus.ACTIVE]: 'Access',
  [UserStatus.PENDINGACTIVATION]: 'Activate',
  [UserStatus.DEACTIVATED]: 'Reactivate',

  // Deprecated post-MFA
  [UserStatus.PENDINGREACTIVATION]: 'Activate',
  [UserStatus.LOCKED]: 'Activate',
  [UserStatus.PASSWORDCHANGEREQUIRED]: 'Activate',
  [UserStatus.UNKNOWN]: 'Activate',

  // This should never get hit as deleted accounts are filtered out
  [UserStatus.DELETED]: '',
};

// This util converts the passed in user accounts data into rows for the dashboard table
export function userAccountsRows(
  userAccountsData: DashboardUser[],
  companyNames: CompanyName[],
  onClick: (userId: string) => Promise<void>,
): UserRow[] {
  return userAccountsData.map((userAccount) => {
    const { accountId, userStatus, userRole, userId } = userAccount;
    const companyName = companyNames?.find((c) => accountId === c?.accountId)?.companyName;
    return {
      userId: (
        <Button
          variant="tertiary"
          className="user-accounts-table-access-button link"
          size="small"
          title={`Login as ${userAccount.logonId} and redirect to home page`}
          onClick={(): void => {
            onClick(userId);
          }}
          data-testid="btn-logonId"
        >
          {userAccount.logonId}
        </Button>
      ),
      userStatus: <StatusTag status={userStatus as UserStatus} />,
      userRole: displayTextForEverifyUserRole(userRole),
      companyName: companyName || defaultFieldValue,
      actions: (
        <Button
          variant="tertiary"
          className="user-accounts-table-access-button"
          size="small"
          title={`Login as ${userAccount.logonId} and redirect to home page`}
          iconLeft={<Icon name={USER_ACCOUNT_ICONS[userStatus as string] ?? 'login'} className="user-access-icon" />}
          onClick={(): void => {
            onClick(userId);
          }}
          data-testid="btn-access"
        >
          {USER_ACCOUNT_TEXT[userStatus as string] ?? 'Access'}
        </Button>
      ),
    };
  });
}

// This util converts the passed in legacy user accounts data into rows for the dashboard link user account table
export function legacyUserAccountsRows(
  userAccountsData: DashboardUser[],
  companyNames: CompanyName[],
  linkActionOnClick: (logonId: string) => void,
  unlinkActionOnClick: (logonId: string) => void,
  triggerPasswordReset: () => void,
): LegacyUserRow[] {
  return userAccountsData.map((userAccount) => {
    const companyName = companyNames?.find((c) => userAccount.accountId === c?.accountId)?.companyName;
    const isPasswordResetRequired =
      userAccount.userStatus === UserStatus.LOCKED || userAccount.userStatus === UserStatus.DEACTIVATED;
    return {
      userId: userAccount.logonId,
      userStatus: <StatusTag status={userAccount.userStatus as UserStatus} />,
      userRole: displayTextForEverifyUserRole(userAccount.userRole),
      companyName: companyName || defaultFieldValue,
      lastLoginDate: userAccount.lastLoginDate ? dateFormat(userAccount.lastLoginDate, 'longDate') : defaultFieldValue,
      actions: (
        <div className="link-accounts-table-actions">
          <Button
            variant="tertiary"
            title={
              isPasswordResetRequired
                ? `Reset Password for User ${userAccount.logonId}`
                : `Link User ${userAccount.logonId} to Login.gov account`
            }
            iconLeft={isPasswordResetRequired ? <Icon name="autorenew" /> : <Icon name="link" />}
            onClick={
              isPasswordResetRequired
                ? (): void => triggerPasswordReset()
                : (): void => linkActionOnClick(userAccount.logonId)
            }
          >
            {isPasswordResetRequired ? 'Reset Password' : 'Link'}
          </Button>
          <Button
            variant="tertiary"
            iconLeft={<Icon name="visibility_off" />}
            title={`Ignore user ${userAccount.logonId}`}
            onClick={(): void => {
              unlinkActionOnClick(userAccount.logonId);
            }}
          >
            Hide
          </Button>
        </div>
      ),
    };
  });
}

// This util converts the passed in data into rows for the user account update table
export function userUpdateRows(profile: DashboardProfile | null, user: User): UserUpdateRow[] {
  const rows: UserUpdateRow[] = [];

  // If name is different, add name row
  if (
    profile?.firstName !== user.firstName ||
    profile?.middleInitial !== user.middleInitial ||
    profile?.lastName !== user.lastName
  ) {
    rows.push({
      userAccountInfo: 'Name',
      previous: displayTextForName(user.firstName, user.lastName, user.middleInitial),
      updated: displayTextForName(profile?.firstName, profile?.lastName, profile?.middleInitial),
    });
  }

  // If email is different, add email row
  if (profile?.email !== user.email) {
    rows.push({
      userAccountInfo: 'Email',
      previous: user.email || defaultFieldValue,
      updated: profile?.email || defaultFieldValue,
    });
  }

  // If phone number is different, add phone number row
  if (
    profile?.phoneNumber?.number !== user.phoneNumber?.number ||
    profile?.phoneNumber?.extension !== user.phoneNumber?.extension
  ) {
    rows.push({
      userAccountInfo: 'Phone Number',
      previous: displayTextForPhoneNumber(user.phoneNumber),
      updated: displayTextForPhoneNumber(profile?.phoneNumber),
    });
  }

  // Return all different rows
  return rows;
}

function getUnlinkedAccounts(): string[] {
  return JSON.parse(localStorage.getItem(UNLINKED_USER_ACCOUNTS_LOCAL_STORAGE_KEY) || '[]');
}

// filter out the users that were removed already
export function hideUnlinkedUserAccounts(userAccounts: DashboardUser[]): DashboardUser[] {
  const unlinkedAccounts: string[] = getUnlinkedAccounts();
  return userAccounts?.filter((userAccount: { logonId: string }) => !unlinkedAccounts.includes(userAccount.logonId));
}

export function unlinkUserAccount(selectedUser: string) {
  const unlinkedAccounts: string[] = getUnlinkedAccounts();
  unlinkedAccounts.push(selectedUser);
  localStorage.setItem(UNLINKED_USER_ACCOUNTS_LOCAL_STORAGE_KEY, JSON.stringify(unlinkedAccounts));
}
